import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserApiService } from '../../../core/services/user-api.service';
import { Router } from '@angular/router';
import { CustomDatePipe } from '../../../core/pipes/custom-date.pipe';
import { CompanyCodeService } from '../../../core/services/adminService/company-code.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule } from 'ng-zorro-antd/table';
import { UserData } from '../../../core/interfaces/user';
import { SessionStorageService } from '../../../core/services/session-storage';
import { LeadBuyerServiceService } from '../../../core/services/Lead Buyers/lead-buyer-service.service';
import { ILeadBuyerRequest } from '../../../core/interfaces/lead-buyer';
import { HttpParams } from '@angular/common/http';
import { CommonService } from '../../../core/services/common.service';
import { constantTxt } from '../../../core/constants/constants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-lead-buyer-manage-request-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomDatePipe,
    NzButtonModule,
    NzSelectModule,
    NzInputModule,
    NzTableModule,
  ],
  templateUrl: './lead-buyer-manage-request-form.component.html',
  styleUrls: ['./lead-buyer-manage-request-form.component.less'],
})
export class LeadBuyerManageRequestFormComponent implements OnInit, OnDestroy {
  isViewMode: boolean = false;
  manageForm!: FormGroup;
  requests: ILeadBuyerRequest[] = [];
  companies: any[] = [];
  buyers: any[] = [];
  purchasingOffices: any[] = [];
  statuses = [
    'ALL',
    'DRAFT',
    'OPEN',
    'IN PROGRESS',
    'PARTIAL QUOTED',
    'CLOSED',
    'CANCELED',
  ];
  itemNo: number = 1;
  userFormAddItemService: any;
  attachmentMap: { [key: string]: boolean } = {};
  userData!: UserData;
  showViewItem: boolean = false;
  customNoDataMessage = constantTxt.noDataFound;

  noDataAvailable = false;
  pageNumber = 1;
  pageSize = 100;
  total = 0;
  selectedRequests: Set<number> = new Set();

  companiesSubscription!: Subscription;
  getOfficesListSubscription!: Subscription;
  getBuyersSubscription!: Subscription;
  getRequestsSubscription!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userApi: UserApiService,
    private readonly router: Router,
    private readonly companyCodeService: CompanyCodeService,
    private readonly leadBuyerServiceService: LeadBuyerServiceService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly commonService: CommonService
  ) {
    this.userData = JSON.parse(
      this.sessionStorageService.getItem('loggedInUser')
    );
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadApiData();
  }

  initializeForm() {
    this.manageForm = this.fb.group({
      companyCode: ['', Validators.required],
      buyer: ['', Validators.required],
      purchasingOffice: ['', Validators.required],
      status: ['OPEN'],
      pnaNo: ['', [Validators.pattern('^\\d+(?:-\\d+)?$')]],
    });
  }

  loadApiData() {
    this.loadDropdowns();
    this.fetchPurchasingOffice();
    this.loadLeadBuyerPna();
  }

  loadDropdowns() {
    this.companiesSubscription = this.companyCodeService
      .getCompanies()
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.companies = data;
          }
        },
        error: () => {
          this.companies = [];
        },
      });

    this.getBuyersSubscription = this.userApi.getBuyers().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.buyers = data;
        }
      },
      error: () => {
        this.buyers = [];
      },
    });

    this.getOfficesListSubscription = this.userApi.getOfficesList().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.purchasingOffices = data;
        }
      },
      error: () => {
        this.purchasingOffices = [];
      },
    });
  }

  fetchPurchasingOffice(): void {
    const po = this.userData?.userPurchasingOffice;
    const poCtrl = this.manageForm.controls['purchasingOffice'];
    if (po) {
      poCtrl.setValue(po);
      poCtrl.disable();
    }
  }

  loadLeadBuyerPna(): void {
    const filter = this.manageForm.getRawValue();

    let params = new HttpParams();
    if (filter.buyer) params = params.set('buyerId', filter.buyer);
    if (filter.companyCode) params = params.set('ccn', filter.companyCode);
    if (filter.purchasingOffice)
      params = params.set('purchasingOffice', filter.purchasingOffice);
    if (filter.status) params = params.set('status', filter.status);
    params = params.set('pageNumber', this.pageNumber);
    params = params.set('pageSize', this.pageSize);

    if (filter.pnaNo) {
      const { pnaNo, PnaYear } = this.commonService.extractPNA(
        filter.pnaNo || ''
      );
      params = params.set('pnaNo', pnaNo || '');
      params = params.set('year', PnaYear);
    }

    this.getRequestsSubscription = this.leadBuyerServiceService
      .getAssignedLeadBuyerPNARequests(params)
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.requests = data.pnaRequests;
            this.total = data.metadata?.totalCount || 1000;
          } else {
            this.requests = [];
          }
        },
        error: () => {},
      });
  }

  // Navigate to view request with clean URL - all data passed through state
  navigateToView(pnaNo: string, pnaYear: string): void {
    const navigationState = {
      mode: 'view',
      role: 'lead-buyer',
      pnaNo: pnaNo,
      year: pnaYear,
      returnUrl: '/lead-buyer/request',
    };

    this.router.navigate(['/lead-buyer/view-request'], {
      state: navigationState,
    });
  }

  // Navigate to edit request with clean URL - all data passed through state
  navigateToEdit(pnaNo: string, pnaYear: string): void {
    const navigationState = {
      mode: 'edit',
      role: 'lead-buyer',
      pnaNo: pnaNo,
      year: pnaYear,
      returnUrl: '/lead-buyer/request',
    };

    this.router.navigate(['/lead-buyer/edit-request'], {
      state: navigationState,
    });
  }

  getStatusFlags(status: string | undefined | null) {
    const s = (status || '').toLowerCase();
    return {
      isDraft: s === 'draft',
      isOpen: s === 'open',
      isDeleted: s === 'deleted',
      isClosed: s === 'closed',
      inProgress: s === 'in progress',
      isPartiallyQuoted: s === 'partial quoted',
    };
  }

  getCcnCode(ccId: any) {
    return this.companies.find((x) => x.ccnId == ccId)?.ccnCode;
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageNumber = pageIndex;
    this.loadLeadBuyerPna();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageNumber = 1; // Reset to first page when page size changes
    this.loadLeadBuyerPna();
  }

  ngOnDestroy(): void {
    if (this.companiesSubscription) {
      this.companiesSubscription.unsubscribe();
    }

    if (this.getOfficesListSubscription) {
      this.getOfficesListSubscription.unsubscribe();
    }

    if (this.getBuyersSubscription) {
      this.getBuyersSubscription.unsubscribe();
    }

    if (this.getRequestsSubscription) {
      this.getRequestsSubscription.unsubscribe();
    }
  }
}
